import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Link2, QrCode, ScanLine, UserPlus } from 'lucide-react';
import ShareIcon from '../ui/ShareIcon';
import { supabase } from '../../supabaseClient';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';
import { useToast } from '../../contexts/ToastContext';
import { useFollowingForUser } from '../../hooks/queries/usePeopleQueries';
import { useFollowWrite } from '../../hooks/mutations/useFollowWrite';
import { openAppSettings, scanCode } from '../../lib/native/priiPeople';
import { parsePriiCode, priiCodeUrl } from '../../lib/people/priiCode';
import { copyTextToClipboard, shareLink } from '../../lib/shareLink';
import Modal, { ModalBody } from '../ui/Modal';
import Button from '../ui/Button';
import Avatar from '../Avatar';
import PriiQr from './PriiQr';
import './People.css';
import { ringColor } from '../../lib/map/faceStyle';

/** One labelled round button: the profile's action row and the code sheet share it. */
export function ActionButton({ icon, label, on = false, onClick, disabled = false }) {
  return (
    <button type="button" className="pp-act" onClick={onClick} disabled={disabled}>
      <span
        className={`pp-act__c ${on ? 'pp-act__c--on' : 'btn-glass-icon btn-glass-icon-neutral'}`}
      >
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

/**
 * Adding people in person (Stage 5): your code (Share on your profile, My code on People) and the scanner (Scan on
 * People, the My code page). openCode() goes to /code, startScan() opens the camera; render `sheets` once on the page.
 * A scan follows them straight away, signal or not - with none, the follow waits on the phone (useFollowWrite).
 */
export function useAddPeople() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { session } = useCurrentUserProfile();
  const me = session?.user?.id ?? null;
  const { data: following = [] } = useFollowingForUser(me, { enabled: Boolean(me), limit: 1000 });
  const { follow, unfollow, isPaused } = useFollowWrite();
  const [found, setFound] = useState(null); // { userId, name, photo, city, followed }
  const [problem, setProblem] = useState(null); // 'not-prii' | 'camera-off'

  // My code is its own page (Sam, 2026-10-30), like the scanner: Share, the People pill and the scanner's button all open it
  const openCode = useCallback(() => {
    if (window.location.pathname !== '/code') navigate('/code');
  }, [navigate]);

  const startScan = useCallback(async () => {
    setFound(null);
    setProblem(null);
    let r;
    try {
      r = await scanCode();
    } catch {
      showToast("Couldn't open the camera.");
      return;
    }
    if (r.unavailable) {
      showToast('Scanning works in the Prii app.');
      return;
    }
    if (r.denied) {
      setProblem('camera-off');
      return;
    }
    if (r.myCode) {
      openCode();
      return;
    } // the camera stays up until the page is under it (PriiPeoplePlugin)
    if (!r.value) return;
    const code = parsePriiCode(r.value);
    if (!code) {
      setProblem('not-prii');
      return;
    }
    if (code.userId === me) {
      showToast("That's your own code.");
      return;
    }
    const already = following.find((p) => p.id === code.userId);
    if (already) {
      showToast(`You follow ${already.display_name || code.name || 'them'}.`);
      navigate(`/profile/${code.userId}`);
      return;
    }
    follow({ id: code.userId, display_name: code.name });
    setFound({ userId: code.userId, name: code.name, photo: null, followed: true });
    // Their photo and name from the server when there is signal; the code's own name carries it otherwise
    supabase
      .from('profiles')
      .select('id, display_name, profile_photo_url')
      .eq('id', code.userId)
      .maybeSingle()
      .then(
        ({ data }) => {
          if (data)
            setFound((f) =>
              f?.userId === data.id
                ? { ...f, name: data.display_name || f.name, photo: data.profile_photo_url }
                : f,
            );
        },
        () => {},
      );
  }, [me, following, follow, navigate, showToast, openCode]);

  const toggleFound = () => {
    if (!found) return;
    const person = { id: found.userId, display_name: found.name, profile_photo_url: found.photo };
    if (found.followed) unfollow(person);
    else follow(person);
    setFound({ ...found, followed: !found.followed });
  };

  const sheets = (
    <>
      <Modal isOpen={Boolean(found)} onClose={() => setFound(null)} variant="sheet" size="sm">
        {found && (
          <ModalBody className="text-center">
            <div className="flex flex-col items-center">
              <span className="pp-avatar" style={{ '--pp-ring': ringColor(found.name || 'Prii') }}>
                <Avatar
                  displayName={found.name || 'Prii'}
                  profilePhotoUrl={found.photo}
                  size="lg"
                />
              </span>
              <b className="mt-3 text-lg text-dark-text">{found.name || 'Prii friend'}</b>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                variant={found.followed ? 'secondary' : 'primary'}
                leftIcon={
                  found.followed ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />
                }
                onClick={toggleFound}
              >
                {found.followed ? 'Following' : 'Follow'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  const id = found.userId;
                  setFound(null);
                  navigate(`/profile/${id}`);
                }}
              >
                View profile
              </Button>
            </div>
            {found.followed && isPaused && (
              <p className="pp-note mt-3">No signal. It goes through when you're back online.</p>
            )}
            <button
              type="button"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-purple-200/90"
              onClick={startScan}
            >
              <ScanLine className="w-4 h-4" />
              Scan another
            </button>
          </ModalBody>
        )}
      </Modal>
      <Modal isOpen={Boolean(problem)} onClose={() => setProblem(null)} variant="sheet" size="sm">
        {problem === 'not-prii' && (
          <ModalBody className="text-center">
            <b className="block text-lg text-dark-text">Not a Prii code</b>
            <p className="mt-1 text-sm text-dark-muted">Try again, or search their name.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Button onClick={startScan} leftIcon={<ScanLine className="w-4 h-4" />}>
                Scan again
              </Button>
            </div>
          </ModalBody>
        )}
        {problem === 'camera-off' && (
          <ModalBody className="text-center">
            <b className="block text-lg text-dark-text">Camera is off for Prii</b>
            <p className="mt-1 text-sm text-dark-muted">
              Turn it on in Settings to scan, or show them your code.
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <Button
                onClick={() => {
                  setProblem(null);
                  openAppSettings();
                }}
              >
                Open Settings
              </Button>
              <Button
                variant="secondary"
                leftIcon={<QrCode className="w-4 h-4" />}
                onClick={() => {
                  setProblem(null);
                  openCode();
                }}
              >
                My code
              </Button>
            </div>
          </ModalBody>
        )}
      </Modal>
    </>
  );

  return { openCode, startScan, sheets };
}

/** Your code: the QR (your profile link) with your name, and Scan, Share, Copy link. The My code page (MyCodePage). */
export function CodeView({ me, name, photo, onScan, showToast }) {
  if (!me) return null;
  const url = priiCodeUrl(me, name);
  return (
    <div className="text-center">
      <div className="pp-codecard">
        <PriiQr value={url} size={230} />
        <div className="pp-codecard__me">
          <Avatar displayName={name || 'Me'} profilePhotoUrl={photo} size="sm" />
          <b className="truncate">{name}</b>
        </div>
      </div>
      <div className="mt-6 flex justify-center gap-2">
        <ActionButton icon={<ScanLine className="w-4 h-4" />} label="Scan" on onClick={onScan} />
        {/* "Send link": your profile on its way to someone (Messages, or any app) - the page itself is what Share on your profile opens */}
        <ActionButton
          icon={<ShareIcon className="w-4 h-4" />}
          label="Send link"
          onClick={() => shareLink({ url, text: 'Add me on Prii' })}
        />
        <ActionButton
          icon={<Link2 className="w-4 h-4" />}
          label="Copy link"
          onClick={async () => {
            try {
              await copyTextToClipboard(url);
              showToast('Link copied.');
            } catch {
              showToast("Couldn't copy the link.");
            }
          }}
        />
      </div>
    </div>
  );
}
